// Terms pass A — read-only register of approved terms, filterable by cohort and status.
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useTermsScope } from "@/lib/termsAccess";
import { ApprovedBadge } from "@/components/terms/StepIndicator";

const COHORTS = ["2026", "2027"];
const STATUSES = ["pending_signature", "active", "voided", "ended", "legacy_pending"];
const sel = "rounded-md border border-[#cfd4dc] px-3 py-2 text-sm";
const day = (iso) => (iso ? iso.slice(0, 10) : "—");

export default function TermsRegister({ embedded = false, defaultCohort = "" }) {
  const { slug } = useParams();
  const { scope, err: scopeErr } = useTermsScope();
  const [cohort, setCohort] = useState(defaultCohort);
  const [status, setStatus] = useState("");
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!scope) return;
    api.get("/terms/register", { params: { tenant_slug: scope.tenant_slug, cohort: cohort || undefined, status: status || undefined } })
      .then(({ data }) => setItems(data.items)).catch((e) => setErr(String(e?.response?.data?.detail || e.message)));
  }, [scope, cohort, status]);
  if (scopeErr || err) return <div className="text-sm text-[#b91c1c]" data-testid="terms-register-error">{scopeErr || err}</div>;
  return (
    <div className="space-y-4" data-testid="terms-register-page">
      {!embedded && <><Link to={`/${slug}/app/commercial-accounts/new`} className="text-sm text-[#0072bc]" data-testid="terms-register-back">← Applications</Link>
      <Link to={`/${slug}/app/commercial-accounts/legacy`} className="text-sm text-[#0072bc] ml-4" data-testid="terms-register-legacy-link">Legacy notice →</Link></>}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        {embedded ? <span /> : <h1 className="text-3xl font-semibold text-[#0e1420]">Terms register</h1>}
        <div className="flex gap-2">
          <select className={sel} value={cohort} onChange={(e) => setCohort(e.target.value)} data-testid="terms-register-cohort-filter">
            <option value="">All cohorts</option>{COHORTS.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select className={sel} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="terms-register-status-filter">
            <option value="">All statuses</option>{STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
        </div>
      </div>
      <div className="bg-white border border-[#e5e7eb] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-[#6b7280]"><tr>{["Company", "Terms", "Credit limit", "Cohort", "Status", "Approved", "Approved by", "Recommended by", "Signed", "Cycle ends"].map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {items && items.length === 0 && <tr><td colSpan={10} className="px-3 py-6 text-[#6b7280]" data-testid="terms-register-empty">No register rows.</td></tr>}
            {(items || []).map((r) => (
              <tr key={r.id} className="border-t border-[#f1f2f4]" data-testid={`terms-register-row-${r.id}`}>
                <td className="px-3 py-2">{r.application_id ? <Link className="text-[#0072bc]" to={`/${slug}/app/terms/${r.application_id}`}>{r.company_name}</Link> : r.company_name}</td>
                <td className="px-3 py-2">{r.terms_label || (r.terms_days ? `Net ${r.terms_days}` : "—")}</td>
                <td className="px-3 py-2">{r.credit_limit != null ? `$${Number(r.credit_limit).toLocaleString()}` : "—"}</td>
                <td className="px-3 py-2">{r.cohort}{r.cohort === "2027" && r.approved_at && <div><ApprovedBadge terms={r.terms_days} date={r.approved_at} by={r.approved_by_name || r.approved_by} testId={`terms-register-approved-${r.id}`} /></div>}</td>
                <td className="px-3 py-2">{r.status}</td>
                <td className="px-3 py-2">{day(r.approved_at)}</td>
                <td className="px-3 py-2">{r.approved_by || "—"}</td>
                <td className="px-3 py-2">{r.recommended_by || "—"}</td>
                <td className="px-3 py-2">{day(r.agreement_signed_at)}</td>
                <td className="px-3 py-2">{day(r.cycle_end_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
